/* eslint-disable react/no-danger */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from "moment";
import queryString from 'query-string';
import {accentFold, stripTags} from "../../helpers/filters";
import CareersArticle from "./CareersArticle";
import CareersSidebar from "./CareersSidebar";
import './Careers.scss';

class Careers extends Component {

    static propTypes = {
        // eslint-disable-next-line react/forbid-prop-types
        items: PropTypes.array.isRequired
    };

    static getKeywords = items => {
        let keywords = [];

        // Fetch new Twill-based keyword structure
        const twillKeywords = items.filter(item => item.tags).map(item => typeof item.tags === 'object' ? item.tags.map(subItem => subItem.name.trim()) : item.tags);
        if (twillKeywords.length) keywords = [...keywords, ...twillKeywords];

        // Flatten and deduplicate the array
        return keywords.flat().filter((value, index, self) => self.indexOf(value) === index);
    }

    state = {
        keywords: [],
        categoryFilter: [],
        textFilter: '',
        imagesRendered: 0
    };

    componentDidMount() {

        const parsed = queryString.parse(location.search);

        // Keywords
        if (parsed.keyword && !this.state.keywords.length) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({keywords: parsed.keyword.split(',').map(word => word.trim())});
        }

        // Title
        if (parsed.title && !this.state.textFilter) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({textFilter: parsed.title});
        }

        // Category
        if (parsed.category && !this.state.categoryFilter.length) {
            // eslint-disable-next-line react/no-did-mount-set-state
            this.setState({categoryFilter: [parsed.category]});
        }
    }

    clearAll = () => {
        this.setState({
            categoryFilter: [],
            textFilter: '',
            keywords: []
        });
    };

    /**
     * Filter posts by selected category
     *
     * @param post
     * @return {boolean}
     */
    filterCategory = (post) => {
        if (!this.state.categoryFilter.length) return true;

        return this.state.categoryFilter.includes(post.category);
    };

    /**
     * Filter posts by selected keywords
     *
     * @param post
     * @return {boolean}
     */
    filterKeywords = (post) => {
        if (!this.state.keywords || !this.state.keywords.length) return true;
        if (!post.tags || !post.tags.length) return false;

        return this.state.keywords.every(current => post.tags.map(tag => tag.name.trim()).includes(current));
    };

    /**
     * Filter posts by title
     *
     * @param post
     * @return {boolean}
     */
    filterTitle = (post) => {
        if (!this.state.textFilter) return true;
        if (!post.title) return false;

        return stripTags(accentFold(post.title)).replace(/\W/g, '').toLowerCase().includes(accentFold(this.state.textFilter).replace(/\W/g, '').toLowerCase());
    };

    /**
     * Require Fields
     *
     * @param post
     * @return {boolean}
     */
    filterRequiredFields = (post) => {
        // Require Title
        if (!post.title) return false;

        return true;
    };

    /**
     * Update State for input by Key
     *
     * @param event
     * @param key
     */
    handleChange = (event, key) => {
        this.setState({[key]: event.target.value});
    };

    /**
     * Allow a Category to be selected for filtering
     *
     * @param event
     * @param category
     */
    handleCategoryChange = (event, category) => {
        const categoryFilter = this.state.categoryFilter;
        if (event.target.checked && !categoryFilter.includes(category)) {
            categoryFilter.push(category);
        } else if (!event.target.checked && categoryFilter.includes(category)) {
            categoryFilter.splice(categoryFilter.indexOf(category), 1);
        }

        this.setState({categoryFilter});
    };

    handleKeywordChange = (event, keyword) => {
        const keywords = this.state.keywords;

        if (event.target.checked && !keywords.includes(keyword)) {
            keywords.push(keywords);
        } else if (!event.target.checked && keywords.includes(keyword)) {
            keywords.splice(keywords.indexOf(keyword), 1);
        }
        this.setState({keywords})
    };

    handleImageLoaded = () => {
        let imagesCurrentlyRendered = this.state.imagesRendered;

        this.setState({imagesRendered: imagesCurrentlyRendered += 1});
    };

    /**
     * Toggle Keywords for filtering
     *
     * @param event
     * @param keyword
     * @param appendOnly
     */
    toggleKeyword = (event, keyword, appendOnly=false) => {
        event.preventDefault();
        const currentlySelected = this.state.keywords;

        const selectedKeywordIndexInArray = currentlySelected.indexOf(keyword);
        if (!appendOnly && selectedKeywordIndexInArray !== -1) {
            currentlySelected.splice(selectedKeywordIndexInArray, 1);
        } else if (selectedKeywordIndexInArray === -1) {
            currentlySelected.push(keyword);
        }

        this.setState({keywords: currentlySelected});
    };

    render() {
        const {items} = this.props;

        // Get unique array of categories
        const categories = items.map(item => item.category).filter((value, index, self) => self.indexOf(value) === index);
        const keywords = Careers.getKeywords(items);

        // Apply currently active filters
        const filteredBlogPosts = items
            .filter(this.filterRequiredFields)
            .filter(this.filterKeywords)
            .filter(this.filterCategory)
            .filter(this.filterTitle);



        const numberOfImages = filteredBlogPosts.filter(item => item.supportingImage && item.supportingImage.entity && item.supportingImage.entity.fieldMediaImage && item.supportingImage.entity.fieldMediaImage.url).length;
        const imagesHaveAllRendered = numberOfImages === this.state.imagesRendered;

        return (
            <div className="container careers">

                <CareersSidebar categories={categories}
                                textFilter={this.state.textFilter}
                                keywords={keywords}
                                keywordsSelected={this.state.keywords}
                                categoryFilter={this.state.categoryFilter}
                                handleChange={this.handleChange}
                                handleCategoryChange={this.handleCategoryChange}
                                toggleKeyword={this.handleKeywordChange}
                                displayClearAll
                                clearAll={this.clearAll}/>

                <div className="careers__main">
                    {filteredBlogPosts && filteredBlogPosts.sort((a, b) => {
                        if (!a || !a.story_date) return -1;
                        if (!b || !b.story_date) return 1;
                        if (moment(a.story_date) === moment(b.story_date)) return 0;
                        return moment(b.story_date) > moment(a.story_date) ? 1 : -1;
                    }) && filteredBlogPosts.map(post => (
                        <CareersArticle key={`kcpostOuter_${post.id}`} post={post} toggleKeyword={this.toggleKeyword} imageLoaded={this.handleImageLoaded} imagesHaveAllRendered={imagesHaveAllRendered}/>
                    ))}
                </div>

            </div>
        )
    }
}

export default Careers;



// WEBPACK FOOTER //
// ./frontend/components/Careers/Careers.jsx